import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import './BulkUpload.css';

const UploadPanel = ({ title, description, endpoint, templateUrl, identifierLabel = 'Migration ID' }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const upload = async () => {
    if (!file) {
      setError('Select an .xlsx file first.');
      return;
    }
    setUploading(true);
    setError('');
    setResult(null);
    const body = new FormData();
    body.append('file', file);
    try {
      const response = await axios.post(endpoint, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      setResult(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data || 'Upload failed. Please verify the file and try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className="bulk-upload-card">
      <div>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      {templateUrl && (
        <a className="btn btn-secondary bulk-template-download" href={templateUrl} download>
          Download {title} Template
        </a>
      )}
      <label className="bulk-file-picker">
        <span>{file ? file.name : 'Choose Excel file'}</span>
        <input
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(event) => {
            setFile(event.target.files?.[0] || null);
            setResult(null);
            setError('');
          }}
          disabled={uploading}
        />
      </label>
      <button className="btn btn-primary" type="button" onClick={upload} disabled={!file || uploading}>
        {uploading ? 'Importing…' : `Upload ${title}`}
      </button>

      {error && <div className="alert alert-error bulk-upload-alert">{String(error)}</div>}
      {result && (
        <div className="bulk-result" aria-live="polite">
          <div className="bulk-result-counts">
            <div><strong>{result.totalRecords}</strong><span>Processed</span></div>
            <div className="bulk-success"><strong>{result.successfulRecords}</strong><span>Successful</span></div>
            <div className="bulk-failed"><strong>{result.failedRecords}</strong><span>Failed</span></div>
          </div>
          {result.failures?.length > 0 && (
            <div className="bulk-failures">
              <h3>Failed records</h3>
              <div className="bulk-table-wrap">
                <table>
                  <thead><tr><th>Excel row</th><th>{identifierLabel}</th><th>Reason</th></tr></thead>
                  <tbody>
                    {result.failures.map((failure, index) => (
                      <tr key={`${failure.rowNumber}-${failure.migrationId}-${index}`}>
                        <td>{failure.rowNumber}</td>
                        <td>{failure.migrationId || '—'}</td>
                        <td>{failure.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

const BulkUpload = () => {
  const { user } = useAuth();
  const isEditor = user?.systemRoles?.some((role) => role.code === 'SYSTEM_EDITOR');
  if (!isEditor) return <Navigate to="/dashboard" replace />;

  return (
    <div className="container bulk-upload-page">
      <header className="bulk-upload-header">
        <div>
          <h1>Bulk Upload</h1>
          <p>Import leads or onboard customers and their historical health readings from Excel.</p>
        </div>
        <a className="btn btn-secondary" href="/dashboard">Back to dashboard</a>
      </header>
      <div className="bulk-upload-notice">
        Migration IDs only connect the two imports. New database IDs are generated automatically for customers.
      </div>
      <div className="bulk-upload-grid">
        <UploadPanel
          title="Leads"
          description="Download the template, add one lead per row, then upload it here. Valid rows create new leads owned by your account; failed rows are reported separately."
          endpoint="/api/bulk-upload/leads"
          templateUrl="/templates/lead-bulk-upload-template.xlsx"
          identifierLabel="Lead"
        />
        <UploadPanel
          title="Customers"
          description="Creates customer accounts and stores each migration ID for later health-data matching. Importing the same migration ID twice is rejected."
          endpoint="/api/bulk-upload/customers"
        />
        <UploadPanel
          title="Health Readings"
          description="Adds dated readings to customers previously imported by this coach. Multiple rows may use the same migration ID."
          endpoint="/api/bulk-upload/health-readings"
        />
      </div>
    </div>
  );
};

export default BulkUpload;
